import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {
    EntityData,
    EntityDataStatus,
    EntityModelStatus,
    provideEntityModel,
    provideSearchModel
} from "@fedeltapointofsale/data-client";
import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {CompositeProductEntityModel} from "../composite-product-entity.model";
import {Config, Plugin} from "../../../config";
import {TaxSearchModel} from "../../../system/tax/tax-search.model";
import {StockCostingService} from "../../../stock/costing/stock-costing.service";
import {PlgSearchPageComponent} from "../../plg/plg-search-page/plg-search-page.component";
import {
    ProductLineSearchPageComponent
} from "../../product-line/product-line-search-page/product-line-search-page.component";
import {ProductLineEditPageComponent} from "../../product-line/product-line-edit-page/product-line-edit-page.component";
import {EditGridComponent} from "../../../framework/entity/edit/edit-grid/edit-grid.component";
import {CompositeProductComponentData, CompositeProductData} from "../composite-product.data";
import {
    SellableEditPriceLevelsComponent
} from "../../sellable/sellable-edit-price-levels/sellable-edit-price-levels.component";
import {debounceTime, first, Subscription} from "rxjs";
import {MetaTagDefinitionData, MetaTagDefinitionReferenceData, MetaTagType} from "../../meta-tag/meta-tag.data";
import {EditPageComponent} from "../../../framework/entity/edit/edit-page/edit-page.component";
import {EditMode} from "../../../framework/entity/edit/edit-page/edit-mode";
import {FUIResponsiveService} from "@fedeltapointofsale/ui";
import {
    DocketSelectorSearchPageComponent
} from "../../../order-processing/docket-selector/docket-selector-search-page/docket-selector-search-page.component";
import {
    DocketSelectorEditPageComponent
} from "../../../order-processing/docket-selector/docket-selector-edit-page/docket-selector-edit-page.component";
import {PlgEditPageComponent} from "../../plg/plg-edit-page/plg-edit-page.component";
import {
    MembershipTypeEditPageComponent
} from "../../../customers/membership-type/membership-type-edit-page/membership-type-edit-page.component";
import {MembershipTypeSearchModel} from "../../../customers/membership-type/membership-type-search.model";
import {MemberTypeLoyaltySpecData} from "../../sellable/sellable.data";
import {MembershipTypeData} from "../../../customers/membership-type/membership-type.data";

@Component({
    selector: "wbo-composite-product-edit-page",
    templateUrl: "./composite-product-edit-page.component.html",
    providers: [
        provideEntityModel(CompositeProductEntityModel),
        provideSearchModel(TaxSearchModel),
        provideSearchModel(MembershipTypeSearchModel),
        StockCostingService
    ]
})
export class CompositeProductEditPageComponent extends AbstractEditPage implements OnInit, OnDestroy {
    Config = Config;
    EditMode = EditMode;
    ProductLineSearchPageComponent = ProductLineSearchPageComponent;
    ProductLineEditPageComponent = ProductLineEditPageComponent;
    PlgSearchPageComponent = PlgSearchPageComponent;
    PlgEditPageComponent = PlgEditPageComponent;
    MetaTagType = MetaTagType;
    DocketSelectorSearchPageComponent = DocketSelectorSearchPageComponent;
    DocketSelectorEditPageComponent = DocketSelectorEditPageComponent;
    Plugin = Plugin;
    MembershipTypeEditPageComponent = MembershipTypeEditPageComponent;

    @ViewChild(EditPageComponent) editPage: EditPageComponent;
    @ViewChild("componentsEditGrid") componentsEditGrid: EditGridComponent;
    @ViewChild(SellableEditPriceLevelsComponent) sellableEditPriceLevels: SellableEditPriceLevelsComponent;

    private componentsChangeSubscription: Subscription;
    private taxChangeSubscription: Subscription;
    private tagsLoadedSubscription: Subscription;
    private subscriptions: Subscription = new Subscription();

    loyaltySpecData: MemberTypeLoyaltySpecData[];
    docketSelectors: MetaTagDefinitionReferenceData[] = [];

    constructor(public model: CompositeProductEntityModel,
                public taxSearchModel: TaxSearchModel,
                private membershipTypeSearchModel: MembershipTypeSearchModel,
                changeDetectorRef: ChangeDetectorRef,
                responsiveService: FUIResponsiveService) {
        super(changeDetectorRef, responsiveService);
        this.model.$data.pipe(first()).subscribe(() => {
            if (this.model.status === EntityModelStatus.INSERT) {
                this.taxSearchModel.search().pipe(first()).subscribe(() => {
                    const taxes = [];
                    this.taxSearchModel.data.filter((tax) => tax.defaultTax).forEach((tax) => {
                        taxes.push(tax);
                    });
                    this.model.data.taxes = taxes;
                });
            }
        });
    }

    ngOnInit(): void {
        this.subscriptions.add(this.membershipTypeSearchModel.search().subscribe(() => {
            this.subscriptions.add(this.model.$data.subscribe(() => {
                this.loyaltySpecData = [];
                this.addMembershipToList(undefined);
                this.membershipTypeSearchModel.data.forEach((membershipType) => {
                    this.addMembershipToList(membershipType);
                });
            }));
        }, () => {
            // @todo should be dealt with
        }));
        this.componentsChangeSubscription = this.model.$collectionChanged("components").pipe(debounceTime(500)).subscribe(() => {
            this.componentsChanged();
        });
        this.taxChangeSubscription = this.model.$collectionChanged("taxes").pipe(debounceTime(500)).subscribe(() => {
            this.taxChanged();
        });
        this.subscriptions.add(this.model.$data.subscribe(() => {
            if (this.tagsLoadedSubscription) {
                this.tagsLoadedSubscription.unsubscribe();
            }
            this.tagsLoadedSubscription = this.model.$collectionLoaded("tags").subscribe(() => {
                this.loadMetaTags();
            });
            // load tags
            const forceLoad = this.model.data.tags;
        }));
    }

    ngOnDestroy(): void {
        if (this.componentsChangeSubscription) {
            this.componentsChangeSubscription.unsubscribe();
        }
        if (this.taxChangeSubscription) {
            this.taxChangeSubscription.unsubscribe();
        }
        this.subscriptions.unsubscribe();
    }

    addMembershipToList(membershipType: MembershipTypeData): void {
        let memberTypeLoyaltySpecData: MemberTypeLoyaltySpecData = this.model.data.memberLoyalty.memberTypeSpecs.find((value) => value.membershipType.membershipTypeId ===
            (membershipType ? membershipType.membershipTypeId : undefined));

        if (!memberTypeLoyaltySpecData) {
            memberTypeLoyaltySpecData = <MemberTypeLoyaltySpecData>EntityData.create(MemberTypeLoyaltySpecData);
            if (membershipType) {
                memberTypeLoyaltySpecData.membershipType.assign(membershipType, EntityDataStatus.LOADED);
            }
        }

        this.loyaltySpecData.push(memberTypeLoyaltySpecData);
    }

    memberLoyaltySaved(data: MemberTypeLoyaltySpecData): void {
        const index = this.model.data.memberLoyalty.memberTypeSpecs.findIndex((value) => value.membershipType.membershipTypeId === data.membershipType.membershipTypeId);
        if (index === -1) {
            this.model.data.memberLoyalty.memberTypeSpecs.push(data);
        }
    }

    componentsChanged(): void {
        this.sellableEditPriceLevels.calculatePricing();
    }

    taxChanged(): void {
        this.sellableEditPriceLevels.calculatePricing();
    }

    addFromProductLine(data: any[]): void {
        let first: boolean = true;
        data.forEach((item) => {
            const component: CompositeProductComponentData = <CompositeProductComponentData>EntityData.create(CompositeProductComponentData);
            component.productLine = item;
            const index: number = this.componentsEditGrid.addRow(component) - 1;
            if (first) {
                setTimeout(() => this.componentsEditGrid.editRow(component, index, true));
                first = false;
            }
        });
    }

    addFromPlg(data: any[]): void {
        let first: boolean = true;
        data.forEach((item) => {
            const component: CompositeProductComponentData = <CompositeProductComponentData>EntityData.create(CompositeProductComponentData);
            component.plg = item;
            const index: number = this.componentsEditGrid.addRow(component) - 1;
            if (first) {
                setTimeout(() => this.componentsEditGrid.editRow(component, index, true));
                first = false;
            }
        });
    }

    addingAnother(previousEntity: CompositeProductData): void {
        this.model.data.primaryCategory.categoryId = previousEntity.primaryCategory.categoryId;
    }

    selectMetaTag(metaTags: any[]): void {
        metaTags.forEach((metaTag) => {
            if (!this.model.data.tags.find((tag) => tag.metaTagDefinitionId === metaTag.metaTagDefinitionId)) {
                const newEntity: MetaTagDefinitionData = <MetaTagDefinitionData>EntityData.create(MetaTagDefinitionData);
                newEntity.assign(metaTag);
                newEntity.localisableContext.universal = true;
                this.model.data.tags.push(newEntity);
                this.editPage.editService.dirty = true;
                this.loadMetaTags();
            }
        });
    }

    metaTagRemoved(metaTag: MetaTagDefinitionData): void {
        this.model.data.tags.splice(this.model.data.tags.findIndex(value => value.metaTagDefinitionId === metaTag.metaTagDefinitionId), 1);
        this.editPage.editService.dirty = true;
        this.loadMetaTags();
    }

    private loadMetaTags(): void {
        this.docketSelectors = this.model.data.tags.filter(value => value.type === this.MetaTagType.DOCKET_SELECTOR);
    }
}
